'use client'

import { Button } from '@repo/ui/components'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/components'
import { Input } from '@repo/ui/components'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components'
import { ScrollArea } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import flags from 'react-phone-number-input/flags'
import PhoneInputCore, { getCountryCallingCode } from 'react-phone-number-input/max'

type CoreProps = React.ComponentProps<typeof PhoneInputCore>
type Country = NonNullable<CoreProps['defaultCountry']>
type PhoneInputProps = Omit<CoreProps, 'onBlur' | 'onChange' | 'value'> &
  Omit<React.ComponentProps<'input'>, 'onBlur' | 'onChange' | 'ref' | 'value'> & {
    buttonClassName?: string
    defaultCountry?: Country
    inputClassName?: string
    onBlur?: () => void
    onChange?: (value: Value) => void
    ref?: React.Ref<React.ComponentRef<typeof PhoneInputCore>>
    value?: Value
    variant?: React.ComponentProps<typeof Input>['variant']
  }

type Value = CoreProps['value']

const PhoneInput = ({
  buttonClassName,
  className,
  defaultCountry,
  inputClassName,
  onBlur,
  onChange,
  ref,
  value,
  variant = 'default',
  ...props
}: PhoneInputProps) => {
  return (
    <div
      className={cn('flex', className)}
      onBlur={e => {
        // Only trigger onBlur if focus leaves the entire component
        if (!e.currentTarget.contains(e.relatedTarget)) {
          onBlur?.()
        }
      }}
    data-sentry-component="PhoneInput" data-sentry-source-file="phone-input.tsx">
      <PhoneInputCore
        {...props}
        className="flex w-full"
        countrySelectComponent={selectProps => (
          <CountrySelect {...selectProps} buttonClassName={buttonClassName} variant={variant} />
        )}
        defaultCountry={
          typeof value === 'string' && value.startsWith('+') ? undefined : defaultCountry
        }
        flagComponent={FlagComponent}
        inputComponent={inputProps => (
          <InputComponent {...inputProps} className={inputClassName} variant={variant} />
        )}
        limitMaxLength
        onChange={val => onChange?.(val ?? '')}
        ref={ref}
        smartCaret={false}
        value={value ?? ''}
      data-sentry-element="PhoneInputCore" data-sentry-source-file="phone-input.tsx" />
    </div>
  )
}

const InputComponent = ({
  className,
  ref,
  variant,
  ...props
}: React.ComponentProps<'input'> & {
  ref?: React.Ref<HTMLInputElement>
  variant?: React.ComponentProps<typeof Input>['variant']
}) => (
  <Input
    className={cn('rounded-s-none rounded-e-lg', className)}
    ref={ref}
    variant={variant}
    {...props}
  data-sentry-element="Input" data-sentry-component="InputComponent" data-sentry-source-file="phone-input.tsx" />
)

type CountryEntry = { label: string; value: Country | undefined }

type CountrySelectProps = {
  buttonClassName?: string
  disabled?: boolean
  onChange: (country: Country) => void
  options: CountryEntry[]
  value: Country
  variant?: React.ComponentProps<typeof Button>['variant']
}

const CountrySelect = ({
  buttonClassName,
  disabled,
  onChange,
  options: countryList,
  value: selectedCountry,
  variant = 'outline',
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null)
  const [searchValue, setSearchValue] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Popover
      modal
      onOpenChange={open => {
        setIsOpen(open)
        open && setSearchValue('')
      }}
      open={isOpen}
    data-sentry-element="Popover" data-sentry-component="CountrySelect" data-sentry-source-file="phone-input.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="phone-input.tsx">
        <Button
          className={cn(
            'flex gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10',
            buttonClassName,
          )}
          disabled={disabled}
          type="button"
          variant={variant}
        data-sentry-element="Button" data-sentry-source-file="phone-input.tsx">
          <FlagComponent country={selectedCountry} countryName={selectedCountry} data-sentry-element="FlagComponent" data-sentry-source-file="phone-input.tsx" />
          <ChevronsUpDown
            className={cn('-mr-2 size-4 opacity-50', disabled ? 'hidden' : 'opacity-100')}
          data-sentry-element="ChevronsUpDown" data-sentry-source-file="phone-input.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-75 p-0" data-sentry-element="PopoverContent" data-sentry-source-file="phone-input.tsx">
        <Command data-sentry-element="Command" data-sentry-source-file="phone-input.tsx">
          <CommandInput
            onValueChange={value => {
              setSearchValue(value)
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector<HTMLDivElement>(
                    '[data-slot="scroll-area-viewport"]',
                  )
                  if (viewportElement) {
                    viewportElement.scrollTop = 0
                  }
                }
              }, 0)
            }}
            placeholder="Search country..."
            value={searchValue}
          data-sentry-element="CommandInput" data-sentry-source-file="phone-input.tsx" />
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="phone-input.tsx">
            <ScrollArea className="h-72" ref={scrollAreaRef} data-sentry-element="ScrollArea" data-sentry-source-file="phone-input.tsx">
              <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="phone-input.tsx">No country found.</CommandEmpty>
              <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="phone-input.tsx">
                {countryList.map(({ label, value }) =>
                  value ? (
                    <CountrySelectOption
                      country={value}
                      countryName={label}
                      key={value}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                      selectedCountry={selectedCountry}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps {
  country: Country
  countryName: string
  onChange: (country: Country) => void
  onSelectComplete: () => void
  selectedCountry: Country
}

const CountrySelectOption = ({
  country,
  countryName,
  onChange,
  onSelectComplete,
  selectedCountry,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country)
    onSelectComplete()
  }

  return (
    <CommandItem className="gap-2" onSelect={handleSelect} data-sentry-element="CommandItem" data-sentry-component="CountrySelectOption" data-sentry-source-file="phone-input.tsx">
      <FlagComponent country={country} countryName={countryName} data-sentry-element="FlagComponent" data-sentry-source-file="phone-input.tsx" />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      data-sentry-element="CheckIcon" data-sentry-source-file="phone-input.tsx" />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: { country: Country; countryName: string }) => {
  const Flag = flags[country]
  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full" data-sentry-component="FlagComponent" data-sentry-source-file="phone-input.tsx">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
export type { Country }
